import { useFeatureIsOn } from '@growthbook/growthbook-react';
import WarningIcon from '@mui/icons-material/Warning';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useReportMonitors } from '@/monitoring/useReportMonitors';
import { useReportDeleteAll } from '@/reports/useReportDelete/useReportDelete';
import {
  Text,
  Icons,
  Box,
  LoadingReportCard,
  Button,
  Modal,
} from '@/theme/index';
import { ResponseError } from '@/utils/index';
import { RecentReportSerialized } from './Interfaces';
import { ReportList, TextWithIcon } from './components';

type RecentReportsProps = {
  reportList: RecentReportSerialized[];
  /** If true, the component is in loading state. */
  isLoading?: boolean;
  editReportsMode: boolean;
  FilterComponent: JSX.Element;
};

function LoadingReports() {
  return (
    <Box mt={2.5}>
      <LoadingReportCard />
      <LoadingReportCard />
      <LoadingReportCard />
    </Box>
  );
}

type DeleteAllReportsModalProps = {
  /** If true, the modal is open */
  isOpen: boolean;
  /** Uncontrolled function for changing the value of 'isOpen' */
  setIsOpen: (isOpen: boolean) => void;
  /** Function for opening the delete all reports modal */
  deleteAllMutation: UseMutationResult<string, ResponseError, void, unknown>;
};

export const DeleteAllReportsModal = ({
  isOpen,
  setIsOpen,
  deleteAllMutation,
}: DeleteAllReportsModalProps) => {
  const queryClient = useQueryClient();
  return (
    <Modal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      title="Are you sure you want to delete all of your reports?"
      maxWidth={false}
      hasCloseIcon
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '320px',
          },
          '#modal-title': {
            textAlign: 'left',
            marginTop: '0',
            maxWidth: '300px',
          },
        },
      }}
    >
      <Box display="flex" flexDirection="column">
        <Box
          display="flex"
          justifyContent="center"
          mb={2.5}
          sx={(theme) => ({
            backgroundColor: theme.palette.error.light,
            borderRadius: '4px',
            border: `1px solid ${theme.palette.error.main}`,
            padding: theme.spacing(1),
          })}
        >
          <WarningIcon color="error" />
          <Text ml={1}>You are about to delete ALL of your reports.</Text>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Button
            variant="outlined"
            color="success"
            sx={{ width: '45%', marginRight: '10%' }}
            onClick={() => {
              deleteAllMutation.mutate();
              queryClient.removeQueries({ queryKey: ['request', 'bbq'] });
              setIsOpen(false);
            }}
          >
            Delete all
          </Button>
          <Button
            variant="contained"
            color="success"
            sx={{ width: '45%' }}
            onClick={() => setIsOpen(false)}
          >
            Go back
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

type ButtonProps = { onClick: () => void };

const DeleteAllButton = ({ onClick }: ButtonProps) => (
  <Button onClick={onClick} variant="contained" color="success" size="small">
    <Box display="flex" alignItems="center">
      <Icons.Edit />
      <Text ml={0.5}>Delete All</Text>
    </Box>
  </Button>
);

export const RecentReports = ({
  isLoading,
  reportList,
  editReportsMode,
  FilterComponent,
}: RecentReportsProps) => {
  const queryClient = useQueryClient();
  const [isDeleteAllReportsModalOpen, setIsDeleteAllReportsModalOpen] =
    useState(false);
  const [currentReports, setCurrentReports] = useState(reportList);
  useEffect(() => setCurrentReports(reportList), [reportList]);
  const deleteCurrentReport = (permalink: string) =>
    setCurrentReports(
      currentReports.filter(
        (report: RecentReportSerialized) => report.id !== permalink
      )
    );

  const deleteAllMutation = useReportDeleteAll(setCurrentReports);
  const [monitorCount, setMonitorCount] = useState(0);
  const { currentMonitors } = useReportMonitors();
  const noMonitors = currentMonitors?.length === 0;

  const isInSuggestedReportsVariation = useFeatureIsOn('oar-1064');

  useEffect(() => {
    queryClient.removeQueries({ queryKey: ['request', 'bbq'] });
  }, []);

  useEffect(() => {
    if (currentMonitors?.length || noMonitors) {
      const maxMonitors = 10;
      const monitorsLength = currentMonitors?.length || 0;
      const remainingMonitors = maxMonitors - monitorsLength;
      setMonitorCount(remainingMonitors);
    }
  }, [currentMonitors, noMonitors]);

  return (
    <Box>
      <Box display="flex" flexDirection="column">
        <TextWithIcon variant="h1">
          <Icons.Restore color="primary" fontSize="large" /> Recent Reports
        </TextWithIcon>
        <Box display="flex" justifyContent="space-between">
          <Text>You have {monitorCount} monitors available</Text>
          <DeleteAllButton
            onClick={() => setIsDeleteAllReportsModalOpen(true)}
          />
        </Box>
      </Box>
      {FilterComponent}
      {isLoading ? (
        <LoadingReports />
      ) : (
        <>
          {isInSuggestedReportsVariation && (
            <Text variant="h2">Recently Viewed</Text>
          )}
          <ReportList
            reportList={currentReports}
            deleteCurrentReport={deleteCurrentReport}
            editReportsMode={editReportsMode}
          />
        </>
      )}
      <DeleteAllReportsModal
        isOpen={isDeleteAllReportsModalOpen}
        setIsOpen={setIsDeleteAllReportsModalOpen}
        deleteAllMutation={deleteAllMutation}
      />
    </Box>
  );
};

export default RecentReports;
